import { z } from 'zod';

// ---- Shared building blocks ----

const idNameSchema = z.object({
    id: z.number().int(),
    name: z.string(),
});

// ---- Dashboard Filters ----

export const dashboardFiltersResponseSchema = z.object({
    isSubscriptionProjectWidgetActive: z.boolean().default(false),
    accessibleLocationCount: z.number().int().default(0),
    filters: z.object({
        brands: z.array(idNameSchema).default([]),
        areas: z.array(idNameSchema).default([]),
        regions: z.array(idNameSchema).default([]),
        locations: z.array(idNameSchema).default([]),
        locationsByAbbreviation: z.array(idNameSchema).default([]),
        managementCompanies: z.array(idNameSchema).default([]),
        locationTypes: z.array(idNameSchema).default([]),
        continents: z.array(idNameSchema).default([]),
        disciplines: z.array(idNameSchema).default([]),
        departments: z.array(idNameSchema).default([]),
        phases: z.array(idNameSchema).default([]),
        communities: z.array(idNameSchema).default([]),
        baselines: z.array(idNameSchema).default([]),
        projects: z.array(idNameSchema).default([]),
        tags: z.array(idNameSchema).default([]),
    }).default(() => ({
        brands: [],
        areas: [],
        regions: [],
        locations: [],
        locationsByAbbreviation: [],
        managementCompanies: [],
        locationTypes: [],
        continents: [],
        disciplines: [],
        departments: [],
        phases: [],
        communities: [],
        baselines: [],
        projects: [],
        tags: [],
    })),
});

export type DashboardFiltersResponse = z.infer<typeof dashboardFiltersResponseSchema>;

// ---- Dashboard Settings ----

const dashboardSettingSchema = z.object({
    id: z.string().optional().nullable(),
    name: z.string().optional().nullable(),
    updates: z.number().int().default(0),
    isProjectSummariesActive: z.boolean().default(true),
    isLocationSummariesActive: z.boolean().default(true),
    isMyTasksDueSoonActive: z.boolean().default(true),
    isMilestonesMissedActive: z.boolean().default(true),
    brandIds: z.array(z.number().int()).default([]),
    areaIds: z.array(z.number().int()).default([]),
    regionIds: z.array(z.number().int()).default([]),
    managementCompanyIds: z.array(z.number().int()).default([]),
    locationTypeIds: z.array(z.number().int()).default([]),
    continentIds: z.array(z.number().int()).default([]),
    isMilestonesOnly: z.boolean().default(false),
    disciplineIds: z.array(z.number().int()).default([]),
    departmentIds: z.array(z.number().int()).default([]),
    phaseIds: z.array(z.number().int()).default([]),
    communityIds: z.array(z.number().int()).default([]),
    baselineIds: z.array(z.number().int()).default([]),
    projectIds: z.array(z.number().int()).default([]),
    locationIds: z.array(z.number().int()).default([]),
    tagIds: z.array(z.number().int()).default([]),
});

export const dashboardSettingsResponseSchema = z.array(dashboardSettingSchema);

export type DashboardSetting = z.infer<typeof dashboardSettingSchema>;
export type DashboardSettingsResponse = z.infer<typeof dashboardSettingsResponseSchema>;

// ---- Overview Tabs Config ----

export const availableProjectSchema = z.object({
    id: z.number().int(),
    name: z.string(),
    abbreviation: z.string().default(''),
    description: z.string().default(''),
});

const overviewTabConfigSchema = z.object({
    id: z.number().int(),
    label: z.string(),
    // Backend may send null (not just omit) for these; .default() only fills undefined,
    // so use .nullish() + transform to normalize null/undefined → [].
    selectedProjectIds: z.array(z.number().int()).nullish().transform((v) => v ?? []),
    selectedSectionIds: z.array(z.string()).nullish().transform((v) => v ?? []),
});

export const overviewTabsConfigResponseSchema = z.object({
    availableProjects: z.array(availableProjectSchema).default([]),
    selectedTabId: z.number().int().optional().nullable(),
    overviewConfig: z.object({
        tabs: z.array(overviewTabConfigSchema).optional().nullable(),
    }).default({}),
});

export type AvailableProject = z.infer<typeof availableProjectSchema>;
export type OverviewTabsConfigResponse = z.infer<typeof overviewTabsConfigResponseSchema>;
export type OverviewTabConfig = z.infer<typeof overviewTabConfigSchema>;

// ---- Update Tabs Config (request/response) ----

export const updateTabsConfigRequestSchema = z.object({
    tabId: z.number().int(),
    tabLabel: z.string(),
    selectedProjectIds: z.array(z.number().int()),
    selectedSectionIds: z.array(z.string()),
});

export const updateTabsConfigResponseSchema = z.object({
    success: z.boolean().default(true),
});

export type UpdateTabsConfigRequest = z.infer<typeof updateTabsConfigRequestSchema>;
export type UpdateTabsConfigResponse = z.infer<typeof updateTabsConfigResponseSchema>;

// ---- Set Overview Tab ----

export const setOverviewTabRequestSchema = z.object({
    overviewTabId: z.number().int().optional().nullable(),
    openingsSubTabId: z.number().int().optional().nullable(),
});

export type SetOverviewTabRequest = z.infer<typeof setOverviewTabRequestSchema>;

// ---- Save / Update Settings ----

export const saveSettingsResponseSchema = z.object({
    id: z.string().optional().nullable(),
}).passthrough();

export type SaveSettingsResponse = z.infer<typeof saveSettingsResponseSchema>;

// ---- Check Settings Availability ----

export const checkSettingsAvailabilityResponseSchema = z.array(dashboardSettingSchema);

export type CheckSettingsAvailabilityResponse = z.infer<typeof checkSettingsAvailabilityResponseSchema>;

// ---- Load Locations By Projects ----

export const pieChartDatumSchema = z
    .object({
        id: z.union([z.string(), z.number()]),
        label: z.string().optional().nullable(),
        value: z.number(),
    })
    .passthrough();

export const locationsByProjectsResponseSchema = z
    .object({
        brand: z.array(pieChartDatumSchema).default([]),
        area: z.array(pieChartDatumSchema).default([]),
        region: z.array(pieChartDatumSchema).default([]),
        type: z.array(pieChartDatumSchema).default([]),
        managementCompany: z.array(pieChartDatumSchema).default([]),
    })
    .passthrough();

export type PieChartDatum = z.infer<typeof pieChartDatumSchema>;
export type LocationsByProjectsResponse = z.infer<typeof locationsByProjectsResponseSchema>;

// ---- Get Overview Tab ----
const scoreSchema = z.unknown().transform((value): number | null => {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (typeof value === 'string' && value.trim() !== '') {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
});

export const locationSummarySchema = z
    .object({
        id: z.number().int(),
        name: z.string(),
        abbreviation: z.string().optional().nullable(),
        locationKey: z.string(),
        healthScore: scoreSchema.optional().default(null),
        riskScore: scoreSchema.optional().default(null),
        scoresUnavailableReason: z.string().optional().nullable(),
    })
    .passthrough();

export const overviewTabResponseSchema = z
    .object({
        locations: z.array(locationSummarySchema).default([]),
    })
    .passthrough();

export type LocationSummary = z.infer<typeof locationSummarySchema>;
export type OverviewTabResponse = z.infer<typeof overviewTabResponseSchema>;

// ---- Overview Location Filters (load-locations-by-project body + overview-tab query params) ----

export const overviewLocationFiltersSchema = z.object({
    projectIds: z.array(z.number().int()).default([]),
    brandIds: z.array(z.number().int()).default([]),
    areaIds: z.array(z.number().int()).default([]),
    regionIds: z.array(z.number().int()).default([]),
    managementCompanyIds: z.array(z.number().int()).default([]),
    locationTypeIds: z.array(z.number().int()).default([]),
    continentIds: z.array(z.number().int()).default([]),
    locationIds: z.array(z.number().int()).default([]),
    tagIds: z.array(z.number().int()).default([]),
    disciplineIds: z.array(z.number().int()).default([]),
    departmentIds: z.array(z.number().int()).default([]),
    communityIds: z.array(z.number().int()).default([]),
    phaseIds: z.array(z.number().int()).default([]),
    baselineIds: z.array(z.number().int()).default([]),
    isMilestonesOnly: z.boolean().default(false),
});

export type OverviewLocationFilters = z.infer<typeof overviewLocationFiltersSchema>;

// ---- Overview Location Breakdown ----

// `.nullish().transform(...)` rather than `.default(...)`: a default only fills in for
// `undefined`, so an explicit `null` — which this endpoint sends for the unassigned bucket's
// name and for counts it has nothing to report — would fail the field, fail the row, and
// (via the surrounding array) fail the whole response.
const nullableText = z
    .string()
    .nullish()
    .transform((value) => value ?? '');

const nullableCount = z
    .number()
    .nullish()
    .transform((value) => value ?? 0);

export const locationBreakdownRowSchema = z
    .object({
        groupId: z.number().int(),
        groupName: nullableText,
        healthScore: scoreSchema.optional().default(null),
        riskScore: scoreSchema.optional().default(null),
        scoresUnavailableReason: z.string().optional().nullable(),
        pastDueMilestoneCount: nullableCount,
        upcomingMilestoneCount: nullableCount,
        totalUnassignedCount: nullableCount,
    })
    .passthrough();

export const locationBreakdownResponseSchema = z
    .object({
        groupBy: z.string().default(''),
        // One malformed row must not cost the user the rows that parsed: `.catch(null)` turns a
        // bad row into a hole the transform drops, instead of rejecting the entire response and
        // sending the panel down the "no data available" path.
        groups: z
            .array(locationBreakdownRowSchema.nullable().catch(null))
            .default([])
            .transform((rows) => rows.filter((row): row is LocationBreakdownRow => row !== null)),
    })
    .passthrough();

export type LocationBreakdownRow = z.infer<typeof locationBreakdownRowSchema>;
export type LocationBreakdownResponse = z.infer<typeof locationBreakdownResponseSchema>;
